import React, { useEffect } from 'react';
import { VStack, HStack, Box, Button, Text, SegmentGroup, Icon } from '@chakra-ui/react';
import { LuCalendarDays, LuCreditCard, LuArrowRight } from 'react-icons/lu';
import { useFormContext } from '../context/FormContext';
import { setFrequency, setAmount, setCustomAmount, nextStep } from '../context/formActions';
import { canContinueFromStep } from '../validation';
import type { DonationFrequency } from '@kuow/core';
import { getAmountTiers, donateEvent } from '@kuow/core';
import { AmountSelect } from '../AmountSelect';
import { useDonationFormUi } from '../context/UiContext';

export const AmountStep: React.FC = () => {
  const { state, dispatch } = useFormContext();
  useDonationFormUi();
  const { formData, formDetails, validation, mode } = state;

  useEffect(() => {
    donateEvent('Step Amount', state.placement);
  }, [state.placement]);

  if (!formDetails) {
    return null;
  }

  const isUpgrade = mode === 'monthly-upgrade' || mode === 'billing-update';

  const handleFrequencyChange = (freq: DonationFrequency) => {
    if (isUpgrade) {
      return;
    }
    donateEvent(freq === 'monthly' ? 'Select Monthly' : 'Select One-time', state.placement);
    dispatch(setFrequency(freq));
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      dispatch(setCustomAmount(null));
    } else {
      donateEvent('Select Custom Amount', state.placement);
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 0) {
        dispatch(setCustomAmount(numValue));
      }
    }
  };

  const tierItems = getAmountTiers(formDetails, formData.frequency, { limit: 4 });

  const selectedAmountValue =
    formData.customAmount !== null
      ? 'custom'
      : tierItems.find((item) => formData.amount === item.numericAmount)?.value ?? '';

  const customAmountDisplay =
    formData.customAmount !== null && formData.customAmount > 0
      ? formData.customAmount
      : '';

  const handleAmountChange = (value: string | null) => {
    if (value === 'custom') {
      if (formData.customAmount === null) {
        dispatch(setCustomAmount(0));
      }
      return;
    }

    const item = tierItems.find((tierItem) => tierItem.value === value);
    if (item) {
      donateEvent('Select Tier', state.placement);
      dispatch(setCustomAmount(null));
      dispatch(setAmount(item.numericAmount));
    }
  };

  const handleContinue = () => {
    if (canContinueFromStep('amount', validation)) {
      dispatch(nextStep());
    }
  };

  return (
    <VStack gap={6} align="center" justify="flex-start" textAlign="center">
      <Text fontSize="xl" fontWeight="semibold">
        {mode === 'monthly-upgrade' ? 'Update Your Donation' : 'Make a Donation'}
      </Text>

      {!isUpgrade && (
        <Box w="full">
          <Text fontWeight="semibold" fontSize="sm" mb={1.5}>
            Choose Frequency
          </Text>
          <SegmentGroup.Root
            value={formData.frequency}
            onValueChange={(details) => handleFrequencyChange(details.value as DonationFrequency)}
            size="md"
            colorPalette="river"
            w="full"
            orientation="horizontal"
          >
            <SegmentGroup.Indicator />
            <SegmentGroup.Item value="monthly" flex={1}>
              <SegmentGroup.ItemText>
                <HStack gap={2}>
                  <Icon as={LuCalendarDays} boxSize={4} />
                  <Text as="span">Monthly</Text>
                </HStack>
              </SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
            <SegmentGroup.Item value="one-time" flex={1}>
              <SegmentGroup.ItemText>
                <HStack gap={2}>
                  <Icon as={LuCreditCard} boxSize={4} />
                  <Text as="span">One-time</Text>
                </HStack>
              </SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
          </SegmentGroup.Root>
        </Box>
      )}

      <Box w="full">
        <Text fontWeight="semibold" fontSize="sm" mb={1.5}>
          Choose Amount
        </Text>
        <AmountSelect
          items={tierItems}
          selectedValue={selectedAmountValue}
          onChange={handleAmountChange}
          customAmountValue={customAmountDisplay}
          onCustomAmountChange={handleCustomAmountChange}
          frequency={formData.frequency}
        />
      </Box>

      <Button
        colorPalette="river"
        size="md"
        onClick={handleContinue}
        disabled={!canContinueFromStep('amount', validation)}
        alignSelf="center"
        px={12}
      >
        Continue
        <Icon as={LuArrowRight} />
      </Button>
    </VStack>
  );
};

